import { useMemo } from 'react';

import { Path, useFormContext } from 'react-hook-form';

import { useAppSelector } from 'src/app/store/hooks';
import {
  PurposeEnum,
  usePayersListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { FormControlledTripAddressField } from 'src/common/TripAddressField/FormControlledTripAddressField';
import {
  useAddressAutoFill,
  useAddressData,
  useAddressOptionRenderer,
} from 'src/common/TripAddressField/hooks';
import { useShouldShowRideOrderingPortalFeatures } from 'src/features/rideOrderingPortal/useShouldShowRideOrderingPortalFeatures';

import useSetPickupAddress from './useSetPickupAddress';

import type { TripSubscriptionFormValues } from '../FormValues';

export function PickupAddressContextField() {
  return (
    <TripSubscriptionAddressContextField
      fieldName="pickup"
      label="Pickup Address *"
      purpose="pickup"
      autoSetPickupAddress
    />
  );
}

export function DropoffAddressContextField() {
  return (
    <TripSubscriptionAddressContextField
      fieldName="dropoff"
      label="Dropoff Address *"
      purpose="dropoff"
    />
  );
}

export interface TripSubscriptionAddressContextFieldProps {
  fieldName: 'pickup' | 'dropoff';
  label: string;
  purpose: PurposeEnum;
  autoSetPickupAddress?: boolean;
}

export function TripSubscriptionAddressContextField({
  fieldName,
  label,
  purpose,
  autoSetPickupAddress = false,
}: TripSubscriptionAddressContextFieldProps) {
  const shouldShowRideOrderingPortalFeatures =
    useShouldShowRideOrderingPortalFeatures();
  const { setValue, watch } = useFormContext<TripSubscriptionFormValues>();
  const { selectedPassenger } = useAppSelector(
    (state) => state.tripSubscription
  );

  const { selectedPayer } = useSelectedPayer();

  const { recentAddressResponse, additionalResultsQueryFn } = useAddressData({
    selectedPassenger,
    selectedPayer,
    skipRecentAddresses: shouldShowRideOrderingPortalFeatures,
  });

  const addressDetailsFieldName: Path<TripSubscriptionFormValues> = `${fieldName}.address_details`;
  const driverNotesFieldName: Path<TripSubscriptionFormValues> = `${fieldName}.driver_notes`;
  const { handleLocationChange } =
    useAddressAutoFill<TripSubscriptionFormValues>({
      recentAddressResponse,
      setValue,
      addressDetailsFieldName,
      driverNotesFieldName,
    });

  const { renderOption } = useAddressOptionRenderer(recentAddressResponse);

  useSetPickupAddress({
    recentAddressResponse,
    setValue,
    enabled: autoSetPickupAddress,
  });

  return (
    <FormControlledTripAddressField
      name={`${fieldName}.address`}
      label={label}
      value={watch(`${fieldName}.address`)}
      purpose={purpose}
      onLocationChange={handleLocationChange}
      additionalResultsQueryFn={additionalResultsQueryFn}
      renderOption={renderOption}
    />
  );
}

const useSelectedPayer = () => {
  const { watch } = useFormContext();
  const formPayer = watch('pricing.payer');
  const { data: payers } = usePayersListQuery(
    {},
    {
      refetchOnMountOrArgChange: true,
    }
  );
  const allPayers = useMemo(() => payers?.results ?? [], [payers]);

  const getPayerById = useMemo(
    () => (id: string | undefined) => {
      if (!id) return undefined;
      return allPayers.find((payer) => payer.id === id);
    },
    [allPayers]
  );

  const selectedPayer = useMemo(
    () => getPayerById(formPayer),
    [getPayerById, formPayer]
  );

  return {
    selectedPayer,
  };
};
